import CloseIcon from '@mui/icons-material/Close';
import { Box, Grid, IconButton, Paper, Typography } from '@mui/material';
import React, { useRef, useState } from 'react';
import CustomButton from './Button';

interface FileUploaderProps {
  maxAttachments?: number;
  accept?: string;
  onChange: (files: File[]) => void;
}

const FileUploader: React.FC<FileUploaderProps> = ({
  maxAttachments = 3,
  accept = 'image/png, image/jpeg, application/pdf',
  onChange
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    const newFiles = [...files, ...selectedFiles].slice(0, maxAttachments);
    setFiles(newFiles);
    onChange(newFiles);
  };

  const handleRemove = (index: number) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    onChange(updatedFiles);
  };

  return (
    <div>
      <Grid container spacing={2} justifyContent={{ lg: 'start', sm: 'space-around' }}>
        {files.map((file, index) => (
          <Grid item xs={6} md={4} lg={3} key={index}>
            <Paper
              sx={{
                position: 'relative',
                width: 120,
                height: 120,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 2,
                overflow: 'hidden'
              }}>
              {/* Show Image */}
              {file.type.startsWith('image/') ? (
                <Box
                  component="img"
                  src={URL.createObjectURL(file)}
                  alt={file.name}
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain'
                  }}
                />
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    color: '#666',
                    textAlign: 'center',
                    px: 1
                  }}>
                  📄 {file.name}
                </Typography>
              )}

              {/* Remove Button */}
              <IconButton
                size="small"
                onClick={() => handleRemove(index)}
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  bgcolor: 'error.main',
                  color: 'white',
                  '&:hover': { bgcolor: 'error.dark' }
                }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ marginTop: 2 }}>
        <span style={{ margin: 'auto 10px' }}>
          {files.length > 0 ? `${files.length} file(s) selected` : 'No File Selected'}
        </span>

        <input
          style={{ display: 'none' }}
          type="file"
          id="file-input"
          multiple
          ref={fileInputRef}
          onChange={handleChange}
          accept={accept}
        />

        <CustomButton
          label="Choose file"
          onClick={handleClick}
          disabled={files.length >= maxAttachments}
        />
      </Box>
    </div>
  );
};

export default FileUploader;
